import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Target, Swords, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { getRecent, setRecent, addRecent } from "../lib/recentSessions";

const OLD_KEY = "bowlink_last_spectate";

function serieTotal(serie) {
  return (serie || []).reduce((s, end) => s + (end || []).reduce((x, v) => x + (v || 0), 0), 0);
}
function soloScore(session) {
  const raw = session?.scores || [];
  if (!raw.length) return 0;
  if (Array.isArray(raw[0]?.[0])) return raw.reduce((t, serie) => t + serieTotal(serie), 0);
  return serieTotal(raw);
}

export default function MatchJoin() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  // Charge et vérifie les sessions actives récentes
  useEffect(() => {
    // migration de l'ancien format (spectateur solo unique)
    try {
      const old = JSON.parse(localStorage.getItem(OLD_KEY));
      if (old?.code) { addRecent("solo", old.code); localStorage.removeItem(OLD_KEY); }
    } catch { /* */ }

    const entries = getRecent();
    if (!entries.length) { setLoadingList(false); return; }

    Promise.all(entries.map(async (e) => {
      try {
        if (e.kind === "solo") {
          const { data } = await api.get(`/solo/spectate/${e.code}`);
          if (data.status === "active") return { ...e, data };
        } else {
          const { data } = await api.get(`/matches/by-code/${e.code}`);
          return { ...e, data };
        }
      } catch { /* inactive ou introuvable */ }
      return null;
    })).then((res) => {
      const alive = res.filter(Boolean);
      setSessions(alive);
      setRecent(alive.map((a) => ({ kind: a.kind, code: a.code })));
      setLoadingList(false);
    });
  }, []);

  const join = async () => {
    if (!code.trim()) return;
    setJoining(true);
    const upper = code.trim().toUpperCase();
    try {
      // 1. Spectateur d'une session solo
      try {
        await api.get(`/solo/spectate/${upper}`);
        addRecent("solo", upper);
        navigate(`/solo/spectate/${upper}`);
        return;
      } catch (err) {
        if (err.response?.status !== 404) throw err;
      }
      // 2. Match
      const { data } = await api.post("/matches/join", { code: upper });
      addRecent("match", upper);
      navigate(`/match/${data.match_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("match_join.not_found"));
    } finally {
      setJoining(false);
    }
  };

  const openSession = async (s) => {
    if (s.kind === "solo") { navigate(`/solo/spectate/${s.code}`); return; }
    try {
      const { data } = await api.post("/matches/join", { code: s.code });
      navigate(`/match/${data.match_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("match_join.not_found"));
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain text-white">
      <header className="sticky top-0 z-20 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <button data-testid="join-back" onClick={() => navigate("/dashboard")} className="p-2 hover:bg-zinc-900 rounded-md">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="font-heading font-black text-xl uppercase tracking-tight">{t("match_join.title")}</span>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-16 space-y-8">
        <div>
          <div className="text-cyan-400 text-xs uppercase tracking-[0.3em] mb-2">{t("match_join.label")}</div>
          <h1 className="font-heading font-black text-5xl uppercase tracking-tight">{t("match_join.heading")}</h1>
          <p className="mt-3 text-zinc-400">{t("match_join.desc")}</p>
        </div>

        <input
          data-testid="join-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && join()}
          placeholder="XXXXXX"
          maxLength={8}
          className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-cyan-400 outline-none rounded-md px-6 py-6 text-center font-heading font-black text-5xl tracking-[0.3em] uppercase"
        />

        <button
          data-testid="join-submit"
          onClick={join}
          disabled={joining || !code.trim()}
          className="w-full bg-cyan-400 text-black hover:bg-cyan-300 font-bold uppercase tracking-wide px-8 py-4 rounded-md disabled:opacity-60 transition-colors"
        >
          {joining ? t("match_join.joining") : t("match_join.cta")}
        </button>

        {!loadingList && sessions.length > 0 && (
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("misc.last_active_session")}</div>
            {sessions.map((s) => (
              <button
                key={`${s.kind}-${s.code}`}
                onClick={() => openSession(s)}
                className="w-full flex items-center gap-4 bg-zinc-900/50 hover:bg-zinc-900 border border-zinc-800 hover:border-cyan-400/40 rounded-md px-4 py-4 transition-all text-left"
              >
                <div className="h-11 w-11 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0">
                  {s.kind === "match"
                    ? <Swords className="h-5 w-5 text-cyan-400" />
                    : <Target className="h-5 w-5 text-cyan-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  {s.kind === "solo" ? (
                    <>
                      <div className="font-heading font-bold uppercase tracking-tight truncate">
                        {s.data.settings?.session_name || t("dashboard.tile_solo_title")}
                      </div>
                      <div className="text-sm text-zinc-400 mt-0.5">
                        {s.data.settings?.ends} × {s.data.settings?.arrows_per_end}
                        {s.data.settings?.series > 1 ? ` · ${s.data.settings.series}` : ""}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-heading font-bold uppercase tracking-tight truncate">
                        {t(`format.${s.data.settings?.format}`)}
                      </div>
                      <div className="text-sm text-zinc-400 mt-0.5 truncate">
                        {(s.data.players || []).map((p) => p.nickname).join(" · ")}
                      </div>
                    </>
                  )}
                </div>
                {s.kind === "solo" && (
                  <div className="text-right shrink-0">
                    <div className="font-heading font-black text-2xl text-cyan-400 tabular-nums">{soloScore(s.data)}</div>
                    <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">pts</div>
                  </div>
                )}
                <div className="text-xs font-mono tracking-widest text-zinc-600 ml-2">{s.code}</div>
                <ExternalLink className="h-4 w-4 text-zinc-600 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
